import { useRef } from "react";
import { CircularProgress, FormHelperText, ListItemText, OutlinedInput, SelectChangeEvent, TextField } from "@mui/material";
import { Button, Chip, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { Checkbox } from "@mui/material";
import { InputEstimateTreeOperationDto, TreeOperationType } from "../../../shared/dtos/estimate-tree-operation.dto";
import { EstimateTreeDto, UpdateEstimateTreeDto, WorkItemType } from "../../../shared/dtos/estimate-tree.dto";
import { ExternalCompanyDto } from "../../../shared/dtos/company.dto";
import { InputDataType } from "../../../shared/services/schema-based-inputs.service";
import { buildInput } from "../../../shared/services/schema-based-inputs.service";
import { TreeOperationSchema } from "../estimate-tree.service";
import { areaSpecFor, treeOperationDisplayLabel } from "../operation-areas.service";
import { OperationAreaChecklist } from "./OperationAreaChecklist";
import { CustomOperationMethodDto } from "../../equipment/dtos/operation-method.dto";
import { logError } from "../../../shared/services/logger.service";

export interface EstimateTreeDetailOperationsProps {
  company: ExternalCompanyDto;
  savedTree: EstimateTreeDto;
  setUpdatedTree: React.Dispatch<React.SetStateAction<UpdateEstimateTreeDto>>;
  updatedOperationsDetails: (InputEstimateTreeOperationDto | undefined)[];
  setUpdatedOperationsDetails: React.Dispatch<React.SetStateAction<(InputEstimateTreeOperationDto | undefined)[]>>;
  selectedOperationIndex: number;
  setSelectedOperationIndex_Internal: React.Dispatch<React.SetStateAction<number>>;
  selectedUpdatedOperationDetails: InputEstimateTreeOperationDto | undefined;
  setSelectedUpdatedOperationDetails: (updater: (prev: InputEstimateTreeOperationDto | undefined) => InputEstimateTreeOperationDto) => void,
  setSelectedUpdatedOperationDetails_Internal: React.Dispatch<React.SetStateAction<InputEstimateTreeOperationDto | undefined>>;
  selectedOperationMethodIds: number[];
  setSelectedOperationMethodIds: React.Dispatch<React.SetStateAction<number[]>>
  availableOperationMethods: CustomOperationMethodDto[];
  operationIsLoading: boolean;
  isSaving: boolean;
  canEdit: boolean;
  /** Library preparation (SQ-500): edit existing operations' facts only; no add, delete or price. The kind may be swapped. */
  factsOnly?: boolean;
  /** Library preparation: an operation was swapped for another kind; `previous` holds the facts the new kind dropped. */
  onOperationSwap?: (previous: InputEstimateTreeOperationDto, next: InputEstimateTreeOperationDto) => void;
  /** Library preparation: per operation id, the facts earlier swaps retired, by kind; a swap to that kind restores them in the draft. */
  retiredDetails?: Record<number, Record<string, {[key: string]: unknown}> | null | undefined>;
  /** Library preparation: delete the selected operation, its price and time going to the others (the caller asks where). */
  onDeleteOperation?: () => void;
  /** Library preparation: add an operation, its price and time taken from the others (the caller asks where). */
  onAddOperation?: () => void;
  /** Library preparation: why adding or deleting is not offered right now (unsaved changes), or null. */
  operationActionsHold?: string | null;
}

/** The operation kinds a work item of each kind may carry (the picker's list; the server's OperationsOffered matches). */
export const getAvailableOperations = (workItemType: WorkItemType): TreeOperationType[] => {
  switch(workItemType) {
    // Android parity (AC-267, 2026-07-23): removal first, then pruning
    // work, then specialty. Stump Grinding / Debris Management are
    // intentionally NOT offered on trees — debris handling is the per-tree
    // Debris Management field instead.
    case WorkItemType.Tree:
    case WorkItemType.TreeGroup:
      return [TreeOperationType.Tree_Removal, TreeOperationType.Thinning, TreeOperationType.Custom_Trim_Prune,
        TreeOperationType.Ground_To_Canopy_Clearance, TreeOperationType.Crown_Reduction, TreeOperationType.Cabling, TreeOperationType.Tree_Health_Care];
    case WorkItemType.FallenTree:
      return [TreeOperationType.Tree_Removal];
    case WorkItemType.Stump:
      // Both kinds, as the server, Android and the record page offer them;
      // the library's picker read this list and could not swap a record
      // from grinding to whole removal when cleaning up data (SQ-643).
      return [TreeOperationType.Stump_Grinding, TreeOperationType.Whole_Stump_Removal];
    case WorkItemType.DebrisPile:
      return [TreeOperationType.Debris_Removal];
    case WorkItemType.Misc:
      return [TreeOperationType.Misc_Task];
    default:
      return [];
  }
}

/** Free-text facts of `from` that `to` has no field for: words a swap would otherwise lose. */
export function orphanedOperationText(from: TreeOperationType, to: TreeOperationType, details: InputEstimateTreeOperationDto['details']): string[] {
  const kept = new Set((TreeOperationSchema[to] ?? []).map(schema => schema.name));
  return (TreeOperationSchema[from] ?? [])
    .filter(schema => schema.dataType === InputDataType.LongText && !kept.has(schema.name))
    .map(schema => details[schema.name])
    .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
    .map(value => value.trim());
}

/**
 * An operation note with words added (each on its own line, unless a line already says it) and lines taken out: what a
 * swap or a type change does to the note when free text leaves a kind's field or comes back to one.
 */
export function noteWith(notes: string, added: string[], removed: string[] = []): string {
  const lines = notes.split('\n').filter(line => !removed.includes(line.trim()));
  return [...lines, ...added.filter(text => !lines.some(line => line.trim() === text))].join('\n').trim();
}

export const operationIsValid = (operation: InputEstimateTreeOperationDto | undefined) => {
  return !!operation?.operationType
}

export const EstimateTreeDetailOperations = ({
  company,
  savedTree,
  setUpdatedTree,
  updatedOperationsDetails,
  setUpdatedOperationsDetails,
  selectedOperationIndex,
  setSelectedOperationIndex_Internal,
  selectedUpdatedOperationDetails,
  setSelectedUpdatedOperationDetails,
  setSelectedUpdatedOperationDetails_Internal,
  selectedOperationMethodIds,
  setSelectedOperationMethodIds,
  availableOperationMethods,
  operationIsLoading,
  isSaving,
  canEdit,
  factsOnly = false,
  onOperationSwap,
  retiredDetails,
  onDeleteOperation,
  onAddOperation,
  operationActionsHold,
}: EstimateTreeDetailOperationsProps) => {

  const setSelectedOperationIndex = (index: number) => {
    setSelectedOperationIndex_Internal(index);
    setSelectedUpdatedOperationDetails_Internal(updatedOperationsDetails[index]);
  }

  // Facts retired by swaps in this editing session, per operation id and kind (library preparation).
  const sessionRetired = useRef<Record<number, Record<string, InputEstimateTreeOperationDto['details']>>>({});

  const getOperationDetailInputs = (operationType?: TreeOperationType) => {
    if(!operationType) return <></>;
    const areaSpec = areaSpecFor(operationType);
    if(areaSpec) {
      return <OperationAreaChecklist
        key={areaSpec.detailsKey}
        spec={areaSpec}
        details={selectedUpdatedOperationDetails!.details}
        disabled={!canEdit}
        factsOnly={factsOnly}
        onPatches={patches =>
          setSelectedUpdatedOperationDetails(prev => ({...prev!, details: {...prev!.details, ...patches}}))}
      />;
    }
    const inputSchema = TreeOperationSchema[operationType];
    if(!inputSchema) {
      // This can happen if we get an operation type that is not in the schema, usually from a previous version of the app
      logError('Operation type not found', {operationType});
      return <></>;
    }
    const fields = inputSchema.map(schema => {
      const shouldDisplay = !schema.conditionalDisplay || schema.conditionalDisplay(selectedUpdatedOperationDetails)
      if(!shouldDisplay) {
        return null;
      }
      // Library preparation shows an unrecorded value as blank instead of the
      // first option or 0, so an unknown fact is never presented or saved as one.
      const defaultValue = factsOnly ? '' :
        schema.options && !schema.noDefaultValue ? schema.options[0] :
        (schema.dataType === InputDataType.Number || schema.dataType === InputDataType.Percentage) ? 0 : '';
      if(!factsOnly && selectedUpdatedOperationDetails!.details[schema.name] === null) {
        setSelectedUpdatedOperationDetails(prev => ({...prev!, details: {...prev!.details, [schema.name]: defaultValue}}))
      }

      // Preparation requires nothing, and blank is a choice: "Unknown".
      return buildInput(factsOnly ? {...schema, required: false, noDefaultValue: false, emptyLabel: 'Unknown'} : schema,
        () => (selectedUpdatedOperationDetails!.details[schema.name] ?? defaultValue) as string | number, (value) => {
        // Unknown ('' or an unparseable percentage) removes the fact rather than storing a value.
        const fact = factsOnly && (value === '' || Number.isNaN(value)) ? undefined : value;
        setSelectedUpdatedOperationDetails(prev => ({...prev!, details: {...prev!.details, [schema.name]: fact}}))
      }, !canEdit);
    });
    return fields;
  }

  const handleOperationTypeChange = (event: SelectChangeEvent) => {
    const operationType = event.target.value as TreeOperationType;
    if (factsOnly) {
      // A swap, not a new operation: the row keeps its id (so its price, time, note, equipment and library
      // approval stay) and its site observations; the old kind's own facts leave the draft (the server retires
      // them by kind), and facts an earlier swap retired for the new kind come back.
      const previous = selectedUpdatedOperationDetails;
      if (!previous || previous.operationType === operationType) return;
      const id = previous.id ?? -1;
      const own = Object.fromEntries(Object.entries(previous.details).filter(([key]) => !key.startsWith('photoContext_')));
      // Swaps before a save stash the facts here, the way the server will; the saved state's retired facts cover the rest.
      const stash = sessionRetired.current[id] ?? (sessionRetired.current[id] = {});
      if (Object.keys(own).length > 0) stash[previous.operationType] = own; else delete stash[previous.operationType];
      const restored = stash[operationType]
        ?? Object.fromEntries(Object.entries(retiredDetails?.[id]?.[operationType] ?? {}).filter(([, value]) => value !== null && value !== undefined)) as InputEstimateTreeOperationDto['details'];
      delete stash[operationType];
      const details = {
        ...Object.fromEntries(Object.entries(previous.details).filter(([key]) => key.startsWith('photoContext_'))),
        ...restored,
      };
      const swapped: InputEstimateTreeOperationDto = {...previous, operationType, details};
      setSelectedUpdatedOperationDetails(() => swapped);
      onOperationSwap?.(previous, swapped);
      return;
    }
    const newOperation: InputEstimateTreeOperationDto = { treeId: savedTree.id, operationType, details: {} }
    if(newOperation.operationType === 'Stump Grinding') {
      // SQ-524 §3.2: the release-of-liability paragraph is the Stump Grinding
      // OPERATION's own note (seeded two blank lines down, like Android), not
      // the work item's general note — V42 moved the existing ones there, and
      // writing it into the work item again would undo that on the next edit.
      newOperation.notes = `

${company.name} will not be liable for any damage to underground utilities or sprinkler pipes that may occur during the stump removal process`;
    }
    setSelectedUpdatedOperationDetails(() => newOperation);
  }

  const selectedOperationIsValid = () => {
    return operationIsValid(selectedUpdatedOperationDetails);
  }

  const handleSelectedOperationChipClick = (index: number) => {
    setSelectedOperationIndex(index);
  }

  const handleAddAnotherOperationClick = () => {
    setUpdatedOperationsDetails(operationDetails => {
      setSelectedOperationIndex(operationDetails.length);
      return [...operationDetails, undefined]
    });
  }

  const handleDeleteOperationClick = () => {
    setUpdatedOperationsDetails(operations => {
      const newIndex = selectedOperationIndex > 0 ? selectedOperationIndex - 1 : 0;
      const newSelectedElementCurrentIndex = selectedOperationIndex > 0 ? selectedOperationIndex - 1 : 1;
      setSelectedUpdatedOperationDetails_Internal(operations[newSelectedElementCurrentIndex]);
      setSelectedOperationIndex_Internal(newIndex);
      return operations.filter((_, i) => i !== selectedOperationIndex);
    });
  }

  const handleOperationMethodsChange = (event: SelectChangeEvent<number[]>) => {
    const { target: { value } } = event;
    const castValue = value as number[];
    const operationMethods = availableOperationMethods.filter(operationMethod => castValue.includes(operationMethod.id));
    setSelectedOperationMethodIds(operationMethods.map(operationMethod => operationMethod.id));
  };

  return (
    <>
      <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px'}} >
        {updatedOperationsDetails.map((operation, i) =>
          <Chip
            key={i}
            color={i === selectedOperationIndex ? 'primary' : 'default'}
            onClick={() => handleSelectedOperationChipClick(i)}
            style={{flexGrow: 0}} label={`${i + 1}: ${operation ? treeOperationDisplayLabel(operation.operationType) : 'Operation'}`}/>)}
      </div>
      <FormControl fullWidth>
        <InputLabel required={false} id="tree-operation-label">Operation</InputLabel>
        <Select
          disabled={!canEdit || (factsOnly && !selectedUpdatedOperationDetails)}
          labelId="tree-operation-label"
          id="tree-operation-select"
          data-testid="operation-type"
          value={selectedUpdatedOperationDetails?.operationType ?? ''}
          label="Operation"
          onChange={handleOperationTypeChange}
        >
          {getAvailableOperations(savedTree.workItemType).map((operationType) =>
            <MenuItem value={operationType} key={operationType}>{treeOperationDisplayLabel(operationType)}</MenuItem>)}
        </Select>
        {factsOnly && getAvailableOperations(savedTree.workItemType).length > 1 && (
          <FormHelperText>
            Choosing another kind swaps this operation in place: its price, time, note, equipment and library approval stay. The old kind's own facts are set aside and come back if it is swapped back; any details text also moves into the operation note.
          </FormHelperText>
        )}
      </FormControl>
      {!operationIsLoading && getOperationDetailInputs(selectedUpdatedOperationDetails?.operationType)}
      {!operationIsLoading && !factsOnly && <TextField
        required={false}
        // disabled={!selectedUpdatedOperationDetails}
        disabled={true}
        label="Estimated Cost ($)"
        variant="outlined"
        value={selectedUpdatedOperationDetails?.estimatedCost ?? ''}
      />}
      {!factsOnly && <Button onClick={handleDeleteOperationClick} disabled={!canEdit || updatedOperationsDetails.length < 2} variant="contained" color="error" style={selectedOperationIsValid() ? ({color: 'black'}) : ({})} component="label">
        {isSaving ? <CircularProgress/> : 'Delete Operation'}
      </Button>}
      {!factsOnly && <Button onClick={handleAddAnotherOperationClick} disabled={!canEdit || !selectedOperationIsValid()} variant="contained" component="label">
        Add Another Operation
      </Button>}
      {factsOnly && (onDeleteOperation || onAddOperation) && (
        // Library preparation: an operation is deleted (its price and time going to the others) or added (taking
        // from them) through the page, against the saved work item; a draft with unsaved changes holds both.
        <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center'}} data-testid="operation-actions">
          {onDeleteOperation && (
            <Button onClick={onDeleteOperation} variant="outlined" color="error" size="small" data-testid="delete-operation"
                    disabled={!canEdit || !!operationActionsHold || updatedOperationsDetails.length < 2 || selectedUpdatedOperationDetails?.id === undefined}>
              Delete this operation
            </Button>
          )}
          {onAddOperation && (
            <Button onClick={onAddOperation} variant="outlined" size="small" data-testid="add-operation" disabled={!canEdit || !!operationActionsHold}>
              Add an operation
            </Button>
          )}
          <FormHelperText sx={{m: 0}}>
            {operationActionsHold ?? (updatedOperationsDetails.length < 2
              ? 'A work item keeps at least one operation: with one, swap its kind instead. Adding one takes its price and time from this one.'
              : 'Deleting moves the price and time to the others; adding takes from them. The work item\'s price and time stay.')}
          </FormHelperText>
        </div>
      )}
      <FormControl fullWidth>
        <InputLabel id="tree-operation-methods-select-label">Operation Methods</InputLabel>
        <Select
          disabled={!canEdit}
          multiple
          labelId="tree-operation-methods-select-label"
          label="Operation Methods"
          value={selectedOperationMethodIds}
          onChange={handleOperationMethodsChange}
          input={<OutlinedInput label="Operation Methods"/>}
          renderValue={(selected) => availableOperationMethods.filter(method => selected.includes(method.id)).map(method => method.name).join(', ')}
        >
          {availableOperationMethods.map((operationMethod) => (
            <MenuItem key={operationMethod.id} value={operationMethod.id}>
              <Checkbox checked={!!selectedOperationMethodIds.find(selectedMethodId => selectedMethodId === operationMethod.id)} />
              <ListItemText primary={operationMethod.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  )
}
